const CLAW_URL = `${process.env.REACT_APP_MyAMP_Url}/api/ams/openclaw-agent-chat`;
const UPLOAD_URL = `${process.env.REACT_APP_MyAMP_Url}/api/ams/openclaw-agent-upload`;
const GATEWAY_STATUS_URL = `${process.env.REACT_APP_MyAMP_Url}/api/ams/openclaw-gateway/status`;
const GATEWAY_START_URL = `${process.env.REACT_APP_MyAMP_Url}/api/ams/openclaw-gateway/start`;
const OPENCLAW_CHECK_URL = `${process.env.REACT_APP_MyAMP_Url}/api/ams/openclaw-check`;
const OPENCLAW_REQUEST_URL = `${process.env.REACT_APP_MyAMP_Url}/api/ams/openclaw-request`;

export async function requestOpenClawAccess(): Promise<boolean> {
  const authToken = localStorage.getItem('authToken') ?? '';
  try {
    const res = await fetch(OPENCLAW_REQUEST_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return res.ok;
  } catch {
    return false;
  }
}
const AMP_ID = '99';
const CHANNEL_ID = '99';
const AI_SERVICE = 'AMS-OPENCLAW-AGENT-CHAT';

export type GatewayStatus = 'running' | 'stopped' | 'starting' | 'unknown';

export async function checkOpenClawConfig(): Promise<boolean> {
  const authToken = localStorage.getItem('authToken') ?? '';
  try {
    const res = await fetch(OPENCLAW_CHECK_URL, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return false;
    const data = await res.json();
    return data.configured === true;
  } catch {
    return false;
  }
}

export async function checkGatewayStatus(): Promise<GatewayStatus> {
  const authToken = localStorage.getItem('authToken') ?? '';
  try {
    const res = await fetch(GATEWAY_STATUS_URL, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return 'unknown';
    const data = await res.json();
    if (data.running === true) return 'running';
    return (data.status as GatewayStatus) ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

export async function startGateway(): Promise<void> {
  const authToken = localStorage.getItem('authToken') ?? '';
  await fetch(GATEWAY_START_URL, {
    method: 'POST',
    headers: { Authorization: `Bearer ${authToken}` },
  });
}

export interface ClawSkill {
  id: string;
  name: string;
  hasScripts: boolean;
}

export async function fetchClawSkillById(id: string): Promise<{ name: string; skill: string } | null> {
  const authToken = localStorage.getItem('authToken') ?? '';
  try {
    const res = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/openclaw-skills/${encodeURIComponent(id)}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return { name: data.name ?? '', skill: data.skill ?? data.content ?? '' };
  } catch {
    return null;
  }
}

export async function createClawSkill(name: string, skill: string): Promise<boolean> {
  const authToken = localStorage.getItem('authToken') ?? '';
  try {
    const res = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/openclaw-skills`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name, skill }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function fetchClawSkills(): Promise<ClawSkill[]> {
  const authToken = localStorage.getItem('authToken') ?? '';
  try {
    const res = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/openclaw-skills`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.skills) ? data.skills : [];
  } catch {
    return [];
  }
}

export interface FileUploadResult {
  ok: boolean;
  filePath: string;
  fileName: string;
  fileSize: number;
}

export async function uploadFileToVM(file: File): Promise<FileUploadResult | null> {
  const authToken = localStorage.getItem('authToken') ?? '';
  try {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(UPLOAD_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
      body: form,
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export interface ClawStreamCallbacks {
  onChunk: (text: string) => void;
  onThought: (thought: string) => void;
  onDone: () => void;
  onError: (err: Error) => void;
}

export async function getMomentumClawSSOToken(): Promise<string | null> {
  const authToken = localStorage.getItem('authToken') ?? '';
  const amsToken = localStorage.getItem('amsToken') ?? '';
  try {
    const res = await fetch(
      `${process.env.REACT_APP_Auth_Url}/api/account/momentumclawsso?nctoken=${encodeURIComponent(amsToken)}`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data?.token ?? null;
  } catch {
    return null;
  }
}

export async function sendClawMessage(
  question: string,
  conversationId: string,
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (err: Error) => void,
  onThought?: (thought: string) => void,
  model: 'openai' | 'claude' = 'openai'
): Promise<void> {
  try {
    const authToken = localStorage.getItem('authToken') ?? '';

    const form = new FormData();
    form.append('human_prompt', question);
    form.append('conversationId', conversationId);
    form.append('ai_service', AI_SERVICE);
    form.append('amp_id', AMP_ID);
    form.append('channel_id', CHANNEL_ID);
    form.append('model', model);

    const response = await fetch(CLAW_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: form,
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const jsonStr = trimmed.slice(5).trim();
        if (!jsonStr) continue;
        try {
          const parsed = JSON.parse(jsonStr);
          
          // Handle thought type messages
          if (parsed.type === 'thought' && parsed.thought && onThought) {
            onThought(parsed.thought);
          }
          
          // Handle answer type messages
          if (parsed.type === 'answer') {
            if (parsed.response) onChunk(parsed.response);
          }
          
          // Handle error messages from API
          if (parsed.error_message && parsed.error_message.trim()) {
            onChunk(`⚠️ ${parsed.error_message}`);
          }
          
          // Handle streaming end without answer (error case)
          if (parsed.responseStreaming === false && !parsed.type) {
            reader.cancel();
            onDone();
            return;
          }
        } catch {
          // skip malformed line
        }
      }
    }

    onDone();
  } catch (err) {
    onError(err as Error);
  }
}
